/**
 * Engagement contracts — slice 11, "email engagement".
 *
 * This is the first file in the package that describes something Wend
 * *collects* rather than something it already knew, and it reverses a position
 * three earlier slices stated in writing. It is therefore mostly an argument
 * about consent and about honesty, and only incidentally a set of shapes.
 *
 * ## What actually changed, and what it costs
 *
 * Slice 4 removed MJML's Google Fonts `<link>`, slice 5 embedded inline images
 * as CID parts, and both wrote down the property that bought: **an email Wend
 * sends makes no network request when it is opened.** That sentence is now
 * conditional. SES implements open tracking by injecting a 1×1 pixel into the
 * HTML and click tracking by rewriting every link through a redirect host, and
 * it does both **server-side, after Wend hands the message over** — so the
 * bytes this codebase composes are unchanged and the promise is broken anyway.
 * The honest form of the sentence is the one this file enforces: *an email Wend
 * sends makes no network request when it is opened **unless that tenant has
 * turned tracking on**, and a tenant who has not is unchanged in every respect.*
 * `./delivery.js`'s header carries the same correction; so does the send path.
 *
 * **Slice 21 added a second condition, and it is the deployment's rather than the
 * tenant's.** Where `TEMPLATE_ASSET_PUBLIC_BASE_URL` is set
 * (`TemplateImageDelivery` is `hosted`), the pictures in an email are no longer
 * CID parts: they are fetched from Wend's image host when the message is opened,
 * and that request carries the reader's IP address whether tracking is on or
 * off. So the sentence becomes *…unless that tenant has turned tracking on, **or
 * the email has pictures and this deployment hosts them***. The disclosure below
 * is therefore chosen by {@link trackingOffDisclosure}, told the delivery mode,
 * and the unconditional wording survives only where it is still true.
 *
 * ## The one property a reviewer should check first
 *
 * **With tracking off, the `SendEmailCommand` must not name a configuration
 * set that can modify a message.** SES adds a pixel or rewrites links for a
 * message sent through a configuration set with an event destination matching
 * OPEN or CLICK, or with VDM engagement tracking on for that set, so which set a
 * send names — if any — is the guarantee Wend's settings control. (What they do
 * not control — engagement tracking on for the whole AWS account, a default set
 * on the sending identity — is what the API's `SES_SEND_TRACKING_OVERRIDES`
 * answers, once a deployment switches it on.) Until slice 19 a tracking-off send named no set at all. Since then
 * it names the delivery reports set when one is set up
 * ({@link deliveryReportsSetNameForTenant}): a set Wend never subscribes to OPEN
 * or CLICK, and stops naming the moment a reconcile finds anything on it that
 * tracks — another destination, or its own VDM engagement tracking. {@link configurationSetNameForSend} is the only supported way to
 * obtain the name, {@link sendCarriesEngagement} is whether it is the tracking
 * set, and the transport spreads the name conditionally rather than passing
 * `undefined`.
 *
 * **Do not try to prove this by comparing MIME.** Wend's raw bytes are byte-
 * identical with tracking on and with it off, because the pixel and the
 * rewritten links are SES's work and happen after the handover. A MIME
 * comparison passes in both cases, which makes it a test that cannot fail. The
 * assertion belongs on the command object the transport builds.
 *
 * ## Four invariants are enforced by the type system, not by a service check
 *
 * A rule enforced in one place is a rule with one place to forget it, so where
 * an invalid combination could be made unrepresentable it was:
 *
 *   1. **Click tracking cannot exist without a verified redirect domain.**
 *      {@link trackingSettingsSchema} is a discriminated union on `click`; the
 *      `click: true` arm's `redirectDomain` is
 *      {@link trackingRedirectDomainVerifiedSchema}, whose `state` is
 *      `z.literal('verified')`. A service that returned click-on with an
 *      unverified domain would fail parsing its own response. See
 *      {@link evaluateTrackingChange} for the refusal a *request* gets, which
 *      is a separate thing: the union says what may exist, the evaluator says
 *      what a person is told.
 *   2. **Tracking cannot be on without a provisioned configuration set.** Same
 *      union, same mechanism — open and click events are emitted *only* by a
 *      configuration set's event destination, so a settings object claiming
 *      tracking with nothing provisioned describes numbers that can never
 *      arrive.
 *   3. **VDM cannot be enabled without its cost having been acknowledged.**
 *      {@link vdmUpdateSchema}'s enabled arm requires `costAcknowledged:
 *      z.literal(true)` — the request body itself cannot express "switch on the
 *      surcharge without showing me the price".
 *   4. **An engagement rate cannot be rendered as an exact one.**
 *      {@link engagementRateSchema} carries `confidence: 'directional'` and
 *      carries **no threshold and no band**, so it is not assignable to
 *      `OverviewRateKpi`; and `OverviewRateKpi` lacks `confidence`, so it is
 *      not assignable here either. Neither component can be handed the other's
 *      number. That is decision 5 held by the compiler rather than by copy a
 *      designer can reword.
 *
 * ## What this file deliberately does not do
 *
 * **It defines no second rate helper.** {@link rateReading} comes from
 * `./overview.js` and is used exactly as it is. The volume floor differs
 * ({@link ENGAGEMENT_MIN_VOLUME_FOR_RATE}) because the argument for the floor
 * differs, but the arithmetic does not: two implementations of "is there enough
 * volume" drift, and the way they drift is that one of them stops checking.
 *
 * `healthBand` is the other half of that pair and it is **deliberately not
 * imported** — which is a decision and not an omission. A band is a verdict,
 * a verdict needs a threshold, and an engagement rate has no threshold: nothing
 * is wrong at 18% opened and nothing is right at 34%. So there is no band on
 * {@link engagementRateSchema} to compute, and the absent import is what stops
 * one being added out of symmetry with the Overview's tiles.
 *
 * **It does not add a member to `overviewResponseSchema`.** That is not
 * timidity, it is the property slice 10's header protects: every fact on
 * `GET /overview` comes from a stored snapshot and **none of it costs a live
 * provider call**. VDM's per-ISP breakdown is read with SESv2
 * `BatchGetMetricData`, which is a live call on the tenant's own AWS account —
 * folding it into `GET /overview` would make drawing the dashboard spend a
 * tenant's rate limit and fail when AWS is slow. {@link engagementResponseSchema}
 * is its own read for that reason, and the Overview page fetches it beside the
 * dashboard rather than inside it.
 *
 * **It carries nothing WhatsApp.** Read receipts are slice 12 and a different
 * provider's consent story.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';
import { awsRegionSchema } from './integration.js';
import { OVERVIEW_ACTIVITY_WINDOW_DAYS, rateReadingSchema, rateReading } from './overview.js';
/*
 * A leaf edge: `template.js` imports nothing that imports this file, so this adds
 * no cycle. Its own cycle with `whatsapp.js` is guarded by `z.lazy()` on its side.
 */
import { templateImageDeliverySchema, type TemplateImageDelivery } from './template.js';

/* -------------------------------------------------------------------------- */
/* The configuration set — the thing that does not exist yet                   */
/* -------------------------------------------------------------------------- */

/**
 * A configuration set name, as SESv2 will accept it.
 *
 * Up to 64 characters of letters, digits, hyphens and underscores — AWS's own
 * rule, restated here rather than discovered at provisioning time as a
 * `BadRequestException` a tenant reads as "Wend is broken".
 *
 * It is a schema and not a bare `z.string()` because this value is handed to
 * `SendEmailCommand` on every send. A name that fails AWS's validation fails
 * *the send*, not the setup — the configuration set is created once and the
 * name is used a million times, so the moment to be strict is the cheap one.
 */
export const configurationSetNameSchema = z
  .string()
  .trim()
  .regex(
    /^[A-Za-z0-9_-]{1,64}$/,
    'A configuration set name is 1–64 letters, digits, hyphens or underscores',
  );

/**
 * The prefix every Wend-provisioned configuration set carries.
 *
 * It exists so that a person looking at the SES console of an AWS account they
 * own can tell which configuration sets Wend made from which ones they made.
 * Under bring-your-own-SES this is *their* account: Wend is a guest in it, and
 * a guest that leaves unlabelled objects behind is the reason people distrust
 * integrations that ask for keys.
 */
export const WEND_CONFIGURATION_SET_PREFIX = 'wend-';

/**
 * The event destination name inside that configuration set. One per set.
 *
 * Named rather than generated for the same reason the set is
 * ({@link configurationSetNameForTenant}): provisioning must be idempotent, and
 * an idempotent create needs a name it can predict on the second run.
 */
export const WEND_EVENT_DESTINATION_NAME = 'wend-sns';

/**
 * The configuration set name for a tenant. **Derived, never random.**
 *
 * A random or timestamped name looks harmless until provisioning fails halfway
 * — the set created, the event destination not — and the retry creates a
 * *second* set. Nothing then deletes the first, it sits in the tenant's account
 * for ever, and if it ever gets named on a send its missing event destination
 * means the events silently stop. A derived name makes the retry find what the
 * first attempt left and finish it, which is the whole difference between
 * provisioning and littering.
 *
 * The tenant id is a UUID, so `wend-` + 36 characters = 41, comfortably inside
 * AWS's 64. Hyphens are legal in the name and the UUID's are left alone.
 */
export function configurationSetNameForTenant(tenantId: string): string {
  return `${WEND_CONFIGURATION_SET_PREFIX}${tenantId}`;
}

/**
 * The second configuration set Wend provisions for a tenant: **delivery reports
 * and nothing else.** Its event destination carries deliveries, bounces,
 * complaints, rejects and delays, never OPEN or CLICK, and it has no tracking
 * options — so a message sent through it is not modified in any way.
 *
 * ## WHY A CONFIGURATION SET AT ALL
 *
 * Until slice 19 a send named a configuration set only when open or click
 * tracking was on, and Wend asked a tenant to set up nothing else — so a
 * workspace with tracking off, which is every workspace by default and the live
 * one, could never receive a delivery report however its SNS topic was wired.
 * Bounces and complaints are not engagement: they are what keeps a sending
 * account alive, and they must not cost a tenant a tracking pixel.
 *
 * SES has one other route to them — a notification topic set on a **verified
 * identity** in the SES console — and the webhook already ingests what it sends
 * (`sesEventFacts` in `./delivery.js`). It was rejected as the route Wend sets
 * up, because it belongs to the identity rather than to Wend's mail. A
 * cooperative bank's SES account may well send other mail from the same
 * domain — one-time passwords, statements — and an identity's topic reports all
 * of it, into a Wend workspace that sent none of it. An identity also has one
 * topic per notification type, so pointing it at Wend takes it away from
 * whatever of the bank's own was already listening. A configuration set reports
 * exactly the messages that name it.
 *
 * ## WHY A SECOND SET, AND NOT THE TRACKING SET WITH TRACKING OFF
 *
 * The tracking set with tracking off *would* modify nothing — reconcile narrows
 * its destination to the delivery types. It was rejected because it does not
 * stay that way under a campaign that is running. Switching tracking on
 * reconciles **that same set** to carry OPEN before the settings are stored, so
 * a running campaign naming it for delivery reports would start receiving a
 * pixel mid-run — the half-tracked campaign `narrowedTracking` exists to
 * prevent, arriving through the one set both purposes shared. A set that never
 * carries engagement cannot be changed into one by anything the tracking
 * switches do, so a campaign on it stays untracked whatever happens around it.
 *
 * `wend-` + 36 + `-reports` = 49 characters, inside AWS's 64. Derived, never
 * random, for {@link configurationSetNameForTenant}'s reason.
 */
export const DELIVERY_REPORTS_SET_SUFFIX = '-reports';

export function deliveryReportsSetNameForTenant(tenantId: string): string {
  return `${configurationSetNameForTenant(tenantId)}${DELIVERY_REPORTS_SET_SUFFIX}`;
}

/**
 * What Wend has actually created in the tenant's AWS account, if anything.
 *
 * A discriminated union rather than a nullable name, because "provisioned, but
 * we do not know the name" and "not provisioned, but here is a name" are both
 * states worth being unable to build. The `provisioned: true` arm is one of the
 * two things {@link configurationSetNameForSend} can read a name out of, and
 * the only one that can put tracking on a message; the other is
 * {@link deliveryReportsSetSchema}'s.
 *
 * `customRedirectDomain` is on the *set*, not on the settings, and it is
 * nullable here on purpose: it records what AWS was actually told, which is not
 * always what the settings now say. A tenant who verified a domain, switched
 * click tracking on, then let the CNAME lapse has a configuration set carrying
 * a domain that no longer resolves — a real state, and one a support
 * conversation needs to be able to see rather than infer.
 */
export const trackingConfigurationSetProvisionedSchema = z
  .object({
    provisioned: z.literal(true),
    name: configurationSetNameSchema,
    /** The event destination carrying the OPEN and CLICK types. */
    eventDestinationName: z.string(),
    /**
     * `TrackingOptions.CustomRedirectDomain` as AWS currently holds it. Null
     * means SES's default host — see {@link TRACKING_DEFAULT_REDIRECT_HOST} and
     * why click tracking is refused in that state.
     */
    customRedirectDomain: z.string().nullable(),
    provisionedAt: isoDateTimeSchema,
    /** The region the set lives in. A configuration set is regional. */
    region: awsRegionSchema,
  })
  .strict();
export type TrackingConfigurationSetProvisioned = z.infer<
  typeof trackingConfigurationSetProvisionedSchema
>;

export const trackingConfigurationSetAbsentSchema = z
  .object({ provisioned: z.literal(false) })
  .strict();

export const trackingConfigurationSetSchema = z.discriminatedUnion('provisioned', [
  trackingConfigurationSetAbsentSchema,
  trackingConfigurationSetProvisionedSchema,
]);
export type TrackingConfigurationSet = z.infer<typeof trackingConfigurationSetSchema>;

/**
 * What Wend has created for delivery reports — the set
 * {@link deliveryReportsSetNameForTenant} names. The tracking set's record
 * without a redirect domain, because this set never has tracking options.
 */
export const deliveryReportsSetProvisionedSchema = z
  .object({
    provisioned: z.literal(true),
    /**
     * Always {@link deliveryReportsSetNameForTenant}'s shape. Refused otherwise,
     * because this is the name every untracked send carries, and a record
     * holding the tracking set's name here would put a set that can carry a
     * pixel on mail from a workspace that never switched tracking on.
     * `integrations_delivery_reports_is_not_the_tracking_set` says the same to
     * the database.
     */
    name: configurationSetNameSchema.refine(
      (name) => name.endsWith(DELIVERY_REPORTS_SET_SUFFIX),
      `A delivery reports configuration set name ends with ${DELIVERY_REPORTS_SET_SUFFIX}`,
    ),
    /** The event destination carrying the delivery types, and only those. */
    eventDestinationName: z.string(),
    provisionedAt: isoDateTimeSchema,
    /** A configuration set is regional; this is the region it lives in. */
    region: awsRegionSchema,
  })
  .strict();
export type DeliveryReportsSetProvisioned = z.infer<typeof deliveryReportsSetProvisionedSchema>;

/**
 * Defaults to not provisioned, so a settings row stored before slice 19 — every
 * row there was — reads as the truth about it: nothing was ever created for
 * delivery reports, and no send names a set that does not exist.
 */
export const deliveryReportsSetSchema = z
  .discriminatedUnion('provisioned', [
    trackingConfigurationSetAbsentSchema,
    deliveryReportsSetProvisionedSchema,
  ])
  .default({ provisioned: false });
export type DeliveryReportsSet = z.infer<typeof deliveryReportsSetSchema>;

/* -------------------------------------------------------------------------- */
/* The redirect domain — a CNAME a person has to go and create                 */
/* -------------------------------------------------------------------------- */

/**
 * SES's own tracking host, and the reason click tracking is refused without a
 * replacement.
 *
 * With no `TrackingOptions.CustomRedirectDomain`, every link in every message
 * is rewritten through `awstrack.me`. To the reader of a mail from their bank,
 * every link now hovers as an unfamiliar third-party domain — which is the
 * textbook description of a phishing mail, and is read that way by the person
 * *and* by the filter. It is the exact opposite of what switching tracking on
 * was meant to buy, so it is not offered as a default and not offered as an
 * option.
 *
 * This constant is here to be quoted in the refusal message, not to be used.
 */
export const TRACKING_DEFAULT_REDIRECT_HOST = 'awstrack.me';

/**
 * A tenant's own redirect subdomain, e.g. `links.acmeretail.in`.
 *
 * **At least three labels**, which is a deliberate approximation. A CNAME
 * cannot coexist with other records at a zone apex, so pointing `acmeretail.in`
 * at AWS would either be refused by their DNS provider or take their website
 * down — and the second outcome is one Wend must not be able to cause by
 * accepting the input. Three labels catches the common `example.in` mistake.
 *
 * It does **not** catch `acmebank.co.in`, which is three labels and still an
 * apex, and no public-suffix list is shipped to make it. That is a decision:
 * a bundled PSL goes stale, and the failure it would prevent is one the
 * tenant's own DNS provider refuses anyway with a clearer message than Wend
 * could write. The screen says "use a subdomain such as `links.` or `email.`"
 * because guidance is the honest tool here, not a regex pretending to know.
 */
export const trackingRedirectDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.){2,}[a-z]{2,63}$/,
    'Use a subdomain you control, e.g. links.acmeretail.in — not the bare domain, because a CNAME cannot sit at a zone apex',
  );

/**
 * The DNS record a person has to create, in the three fields their registrar's
 * form actually has.
 *
 * Carried on the wire rather than derived in the browser. The value depends on
 * the AWS region, AWS has changed tracking hosts before, and a frontend that
 * computes it from a hard-coded string is a frontend that hands somebody a
 * DNS record that silently does nothing. The API learned it from the region on
 * the integration; the screen displays exactly what it was given.
 *
 * `type` is `z.literal('CNAME')` because there is one kind of record here and
 * a field that could say `TXT` invites a copy of DKIM's flow to be pasted in.
 */
export const trackingCnameRecordSchema = z
  .object({
    /** The record name — the subdomain itself, e.g. `links.acmeretail.in`. */
    name: trackingRedirectDomainSchema,
    type: z.literal('CNAME'),
    /** What it points at. AWS's regional tracking host. */
    value: z.string().min(1),
    /**
     * Suggested TTL in seconds. Advisory: registrars disagree about defaults
     * and some do not offer the field at all, so it is a hint the screen may
     * show and never a value anything depends on.
     */
    ttlSeconds: z.number().int().min(60).max(86_400),
  })
  .strict();
export type TrackingCnameRecord = z.infer<typeof trackingCnameRecordSchema>;

/** The TTL Wend suggests. Low, because a mistyped record should be cheap to fix. */
export const TRACKING_CNAME_TTL_SECONDS = 300;

/**
 * The host AWS's documentation gives for a custom open/click redirect domain in
 * a region: `r.<region>.awstrack.me`.
 *
 * **One function, so there is one place to change it.** This is a third party's
 * infrastructure detail, it is not in any SDK type, and it is not something a
 * compiler will tell us about if AWS moves it. The value it produces is stored
 * on {@link trackingCnameRecordSchema} at the moment the flow starts, so a
 * tenant mid-setup keeps seeing the record they were told to create even if
 * this function's answer changes underneath them.
 */
export function trackingCnameValue(region: string): string {
  return `r.${region}.${TRACKING_DEFAULT_REDIRECT_HOST}`;
}

/**
 * Why a redirect domain is not verified. Three different jobs, not one.
 *
 *   - `awaiting_dns` — the record has not appeared yet. Ordinary: DNS
 *     propagation is minutes to hours and this is not an error;
 *   - `record_not_found` — we looked and there is nothing at that name. The
 *     record was not created, or was created at the wrong name (the classic:
 *     the registrar appended the zone, producing
 *     `links.acmeretail.in.acmeretail.in`);
 *   - `record_mismatch` — something is there and it points somewhere else;
 *   - `certificate_pending` — the record is right and AWS has not finished
 *     issuing the certificate for the subdomain. Also ordinary, also not an
 *     error, and worth its own member because the repair is *wait* and the
 *     repair for the two above is *edit DNS*;
 *   - `unavailable` — we could not check. Nothing about the domain is known,
 *     and the screen must not turn that into a red cross.
 */
export const TRACKING_DOMAIN_FAILURE_VALUES = [
  'awaiting_dns',
  'record_not_found',
  'record_mismatch',
  'certificate_pending',
  'unavailable',
] as const;
export const trackingDomainFailureSchema = z.enum(TRACKING_DOMAIN_FAILURE_VALUES);
export type TrackingDomainFailure = z.infer<typeof trackingDomainFailureSchema>;

export const TRACKING_DOMAIN_FAILURE_MESSAGES: Readonly<
  Record<TrackingDomainFailure, string>
> = {
  awaiting_dns:
    'The CNAME record has not reached us yet. DNS changes usually take a few minutes and can take a few hours; nothing is wrong until it has been a day.',
  record_not_found:
    'There is no CNAME record at that name. Check the name your DNS provider saved — several of them append your domain to whatever you type, which turns links.example.in into links.example.in.example.in.',
  record_mismatch:
    'A CNAME record exists at that name but points somewhere else. Replace its value with the one shown here; a subdomain can only have one CNAME.',
  certificate_pending:
    'The record is correct and Amazon is still issuing the certificate for this subdomain. This finishes on its own — there is nothing to change.',
  unavailable:
    'We could not check the record just now. This says nothing about whether it is correct; try again shortly.',
};

/**
 * The redirect domain, in whichever of its four states it is in.
 *
 * A discriminated union rather than a domain plus a `verified` boolean plus a
 * nullable failure code, because that shape can express "verified, and here is
 * why it failed" and this one cannot. The `verified` arm is exported on its own
 * as {@link trackingRedirectDomainVerifiedSchema}, because it is the arm
 * {@link trackingSettingsSchema}'s click-on branch requires *by type* — that is
 * how decision 3 stops being a service check somebody can route around.
 *
 * `not_configured` is the ordinary starting state and not a failure. Open
 * tracking works from it, because the pixel is served by SES and the reader
 * never sees a hostname; only click tracking needs this to reach `verified`.
 */
export const trackingRedirectDomainNotConfiguredSchema = z
  .object({ state: z.literal('not_configured') })
  .strict();

export const trackingRedirectDomainPendingSchema = z
  .object({
    state: z.literal('pending'),
    domain: trackingRedirectDomainSchema,
    /** Exactly what to create. Shown, copied, and not recomputed by the screen. */
    record: trackingCnameRecordSchema,
    requestedAt: isoDateTimeSchema,
    /** Null until the first check has run. Not the same as a failed check. */
    lastCheckedAt: isoDateTimeSchema.nullable(),
    /** Why it is not verified yet. `awaiting_dns` is the normal answer. */
    reason: trackingDomainFailureSchema,
  })
  .strict();

export const trackingRedirectDomainVerifiedSchema = z
  .object({
    state: z.literal('verified'),
    domain: trackingRedirectDomainSchema,
    record: trackingCnameRecordSchema,
    verifiedAt: isoDateTimeSchema,
  })
  .strict();
export type TrackingRedirectDomainVerified = z.infer<
  typeof trackingRedirectDomainVerifiedSchema
>;

/**
 * Verified once, and not any more. Kept apart from `pending` deliberately.
 *
 * A domain that was working and stopped is an incident — links in mail already
 * delivered now point at a host that does not resolve — and a domain that has
 * never worked is a setup step. Collapsing them into `pending` would show the
 * calm "DNS takes a few minutes" copy to a tenant whose live campaign links are
 * broken.
 */
export const trackingRedirectDomainBrokenSchema = z
  .object({
    state: z.literal('broken'),
    domain: trackingRedirectDomainSchema,
    record: trackingCnameRecordSchema,
    /** When it last verified. It did work once, and that matters to the reader. */
    verifiedAt: isoDateTimeSchema,
    lastCheckedAt: isoDateTimeSchema,
    reason: trackingDomainFailureSchema,
  })
  .strict();

/** The redirect domain in any of its four states. */
export const trackingRedirectDomainStateSchema = z.discriminatedUnion('state', [
  trackingRedirectDomainNotConfiguredSchema,
  trackingRedirectDomainPendingSchema,
  trackingRedirectDomainVerifiedSchema,
  trackingRedirectDomainBrokenSchema,
]);
export type TrackingRedirectDomainState = z.infer<typeof trackingRedirectDomainStateSchema>;

/** Builds the record to display, from the subdomain and the integration's region. */
export function trackingCnameRecord(domain: string, region: string): TrackingCnameRecord {
  return {
    name: domain,
    type: 'CNAME',
    value: trackingCnameValue(region),
    ttlSeconds: TRACKING_CNAME_TTL_SECONDS,
  };
}

/* -------------------------------------------------------------------------- */
/* VDM — a surcharge on somebody else's bill                                   */
/* -------------------------------------------------------------------------- */

/**
 * What Virtual Deliverability Manager costs, and what sending costs, in the same
 * unit so the screen can put them side by side.
 *
 * Roughly **$0.07 per 1,000 messages**, against SES's own roughly **$0.10 per
 * 1,000** to send — a surcharge near 70% of the send cost. Under
 * bring-your-own-SES that lands on **the tenant's** AWS bill, not Wend's, which
 * is the whole reason it is their decision and not a default.
 *
 * These are approximations of AWS's list price and are labelled as such
 * wherever they are shown. They are region-dependent, AWS changes them, and a
 * product that prints a stale figure as if it were a quote has told a customer
 * something about their money that is not true. The screen says "about", links
 * to AWS's pricing page, and the figure is re-checked against it before any
 * release that quotes it.
 */
export const VDM_COST_PER_THOUSAND_USD = 0.07;
export const SES_SEND_COST_PER_THOUSAND_USD = 0.1;

/**
 * The one console step Wend does not take for the tenant, written down once.
 *
 * Named as a constant rather than inlined because three places have to say it
 * and they must say the same words: the cost disclosure the switch shows, the
 * sentence the empty ISP panel shows, and the panel on the Integrations screen.
 * A product that asks somebody to go and do something in another console owes
 * them the path, not the noun.
 */
export const VDM_ENABLE_STEP =
  'To turn it on: Amazon SES console → Virtual Deliverability Manager → Get started, which enables it for the whole AWS account.';

/**
 * The disclosure the VDM switch shows. **The switch cannot be flipped without
 * it** — see {@link vdmUpdateSchema}, whose enabled arm requires
 * `costAcknowledged: z.literal(true)`.
 *
 * Written here rather than in the component so the API's audit entry and the
 * screen's copy are the same sentence. A consent recorded against text nobody
 * can retrieve is not a record of anything.
 *
 * ## WHAT THIS SWITCH DOES AND DOES NOT DO, WHICH THIS SENTENCE HAS TO SAY
 *
 * It used to end *"Switching it on changes what Amazon charges you from the
 * moment you do"*, and that was false. Wend never enables VDM. Decision 1 has
 * Wend provision three things in a tenant's AWS account — the configuration set,
 * its event destination, its tracking domain — and `ses.provisioning.ts`'s
 * ownership section puts everything else, *VDM's own attributes named
 * explicitly*, on the console side of the line: this is somebody else's AWS
 * account, VDM is an **account-wide** attribute affecting every message that
 * account sends including mail Wend never saw, and flipping it would be Wend
 * changing another application's bill.
 *
 * So the switch here is Wend's opt-in to *read* the figures, and enabling VDM
 * is a step in the tenant's own console. Saying otherwise recorded a consent to
 * a charge that never arrived, in exchange for a panel that could never fill in
 * — with nothing anywhere naming the step that would have fixed it.
 */
export const VDM_COST_DISCLOSURE = `Virtual Deliverability Manager is billed by Amazon to your own AWS account, at about $${VDM_COST_PER_THOUSAND_USD.toFixed(
  2,
)} per 1,000 messages — on top of the roughly $${SES_SEND_COST_PER_THOUSAND_USD.toFixed(
  2,
)} per 1,000 SES already charges you to send. Wend does not bill it and cannot waive it. Amazon charges it from the moment VDM is enabled in your AWS account, and enabling it is yours to do: Wend reads the figures, it does not switch VDM on. ${VDM_ENABLE_STEP}`;

/**
 * Whether VDM is on, and — if it is — the record that its price was shown.
 *
 * A discriminated union so `enabled: true` cannot exist without
 * `costAcknowledgedAt`. The alternative, a boolean plus a nullable timestamp,
 * lets a migration or a mapper switch on a surcharge on a customer's AWS bill
 * with no evidence anybody was told, and "we believe the screen showed it" is
 * not an answer to that question three months later.
 */
export const vdmSettingsDisabledSchema = z.object({ enabled: z.literal(false) }).strict();

export const vdmSettingsEnabledSchema = z
  .object({
    enabled: z.literal(true),
    /** When the cost disclosure was accepted. Not nullable — see above. */
    costAcknowledgedAt: isoDateTimeSchema,
    /**
     * The exact figure quoted at that moment, in USD per 1,000 messages.
     *
     * Stored rather than re-read from {@link VDM_COST_PER_THOUSAND_USD},
     * because that constant will change and the consent was given against the
     * number that was on the screen at the time.
     */
    costPerThousandUsdAtAcknowledgement: z.number().min(0),
  })
  .strict();

export const vdmSettingsSchema = z.discriminatedUnion('enabled', [
  vdmSettingsDisabledSchema,
  vdmSettingsEnabledSchema,
]);
export type VdmSettings = z.infer<typeof vdmSettingsSchema>;

/* -------------------------------------------------------------------------- */
/* THE SETTINGS — where three invariants stop being service checks             */
/* -------------------------------------------------------------------------- */

/**
 * What a tenant's tracking is set to. **The shape three rules are enforced in.**
 *
 * A discriminated union on `click`, and every field that differs between the
 * arms differs because of a rule:
 *
 *   - the `click: true` arm's `redirectDomain` is
 *     {@link trackingRedirectDomainVerifiedSchema}. Click tracking without a
 *     verified custom domain rewrites every link through
 *     {@link TRACKING_DEFAULT_REDIRECT_HOST}, which is decision 3's refusal;
 *   - the `click: true` arm's `configurationSet` is
 *     {@link trackingConfigurationSetProvisionedSchema}. Click events are
 *     emitted only by an event destination, so click-on with nothing
 *     provisioned promises numbers that cannot arrive.
 *
 * `open` stays a plain boolean in both arms, and that is also a decision:
 * **open tracking may ship without a custom domain**, because the pixel's
 * hostname is never seen by a reader. The asymmetry between open and click is
 * the entire content of decision 3, and it is visible here as the reason one of
 * them constrains the union and the other does not.
 *
 * Open-on-with-nothing-provisioned is left representable, deliberately, and is
 * refused by {@link evaluateTrackingChange} instead. Constraining `open` the
 * same way would need a second discriminator and a four-armed union to say one
 * thing the evaluator already says in a sentence a person can read; the union
 * earns its complexity on `click` because click is the rule with a
 * deliverability consequence, and does not earn it twice.
 *
 * There is no `enabled` master switch. Two booleans, each meaning exactly one
 * mechanism, because "tracking is on" is not a thing SES does — a pixel is one
 * feature and link rewriting is another, and a single switch would make the
 * disclosure in {@link TRACKING_DISCLOSURE} vaguer than the thing it discloses.
 */
export const trackingSettingsClickOffSchema = z
  .object({
    click: z.literal(false),
    /** True injects a 1×1 pixel into every HTML message. Off by default. */
    open: z.boolean(),
    redirectDomain: trackingRedirectDomainStateSchema,
    configurationSet: trackingConfigurationSetSchema,
    /** Slice 19's second set. See {@link deliveryReportsSetNameForTenant}. */
    deliveryReports: deliveryReportsSetSchema,
    vdm: vdmSettingsSchema,
    /** When these settings last changed. Drives "tracking has only been on since…". */
    updatedAt: isoDateTimeSchema.nullable(),
  })
  .strict();

export const trackingSettingsClickOnSchema = z
  .object({
    click: z.literal(true),
    open: z.boolean(),
    /** Verified, by type. This is decision 3. */
    redirectDomain: trackingRedirectDomainVerifiedSchema,
    /** Provisioned, by type. Nothing else emits a Click event. */
    configurationSet: trackingConfigurationSetProvisionedSchema,
    deliveryReports: deliveryReportsSetSchema,
    vdm: vdmSettingsSchema,
    updatedAt: isoDateTimeSchema.nullable(),
  })
  .strict();

export const trackingSettingsSchema = z.discriminatedUnion('click', [
  trackingSettingsClickOffSchema,
  trackingSettingsClickOnSchema,
]);
export type TrackingSettings = z.infer<typeof trackingSettingsSchema>;

/**
 * The state every tenant is in until somebody decides otherwise. **Off.**
 *
 * Exported so the API does not invent a default in a mapper, and so a tenant
 * row that has never been touched and a tenant row that was explicitly turned
 * off produce the identical object. `updatedAt: null` is what tells those two
 * apart, and it is the only thing that does.
 */
export const TRACKING_OFF: TrackingSettings = {
  click: false,
  open: false,
  redirectDomain: { state: 'not_configured' },
  configurationSet: { provisioned: false },
  deliveryReports: { provisioned: false },
  vdm: { enabled: false },
  updatedAt: null,
};

/**
 * Whether this tenant's mail carries anything at all. `open || click`.
 *
 * The predicate every piece of copy branches on. It exists so that the sentence
 * slice 4 wrote — *an email Wend sends makes no network request when it is
 * opened* — can be printed truthfully, by being printed only when this is
 * false **and the deployment embeds pictures** (see {@link trackingOffDisclosure};
 * where pictures are hosted, an email with one fetches it whatever this says).
 */
export function tracksEngagement(settings: TrackingSettings): boolean {
  return settings.open || settings.click;
}

/**
 * Whether a send goes out through the **tracking** set — the only way a message
 * can carry a pixel or rewritten links. The tenant asked for tracking, and the
 * set that does it exists.
 *
 * Asked wherever the question is "is this message tracked": the send log's
 * `tracked`, and `narrowedTracking`'s rule that a run may lose tracking and
 * never gain it. Since slice 19 that is no longer the same question as "does
 * the send name a configuration set" — a tracking-off send names the delivery
 * reports set, and is not tracked.
 */
export function sendCarriesEngagement(settings: TrackingSettings): boolean {
  return tracksEngagement(settings) && settings.configurationSet.provisioned;
}

/**
 * **THE SAFETY PROPERTY OF SLICE 11, as one function — and slice 19's delivery
 * reports, without giving it up.**
 *
 * The configuration set name to put on `SendEmailCommand`, or `null` — and
 * `null` means the field is **omitted entirely**, not set to `undefined`, not
 * set to `''`.
 *
 * SES modifies a message through a configuration set whose event destination
 * matches OPEN or CLICK, or whose own VDM engagement tracking is on — and, beyond
 * anything a set name reaches, when the AWS account has engagement tracking on
 * for every message. So, for what a set name decides:
 *
 *   - **tracking asked for, and the tracking set exists** → the tracking set.
 *     The one case that can modify a message, and only because the tenant
 *     switched it on;
 *   - **otherwise, the delivery reports set exists** → the delivery reports set,
 *     whose destination never carries OPEN or CLICK and which has no tracking
 *     options. It modifies nothing while it holds only what Wend put there;
 *     it is how a bounce reaches Wend. It is recorded only after a reconcile
 *     that found nothing else on it that tracks — no *other* destination
 *     matching OPEN or CLICK, no VDM engagement tracking — because SES honours
 *     those whoever set them.
 *     See {@link deliveryReportsSetNameForTenant} for why it is a separate set;
 *   - **otherwise** → `null`. Naming a set that does not exist makes SES reject
 *     the send outright, and a misconfiguration must never become a delivery
 *     failure.
 *
 * Until slice 19 the second arm did not exist, and the absence of the field was
 * the whole guarantee — which also meant no workspace with tracking off could
 * ever receive a delivery report. The guarantee is now that the tracking set is
 * named only when tracking is asked for; {@link sendCarriesEngagement} is that
 * half on its own.
 *
 * **The test for this asserts on the command object, never on the MIME.** Wend's
 * raw bytes are identical either way — SES injects server-side, after the
 * handover — so a MIME comparison passes whether tracking is on or off, which
 * makes it a test that cannot fail.
 */
export function configurationSetNameForSend(settings: TrackingSettings): string | null {
  if (tracksEngagement(settings) && settings.configurationSet.provisioned) {
    return settings.configurationSet.name;
  }
  if (settings.deliveryReports.provisioned) return settings.deliveryReports.name;
  return null;
}

/* -------------------------------------------------------------------------- */
/* Disclosure — the plain words the switch is obliged to say                   */
/* -------------------------------------------------------------------------- */

/**
 * What the switch tells a person, in the words the rest of this product uses.
 *
 * Here rather than in a component, for two reasons. The API writes an audit
 * entry when tracking changes and it should record the text the person was
 * shown, not a paraphrase. And a tenant who has promised their members no
 * tracking needs Wend's answer to "what exactly does this do" to be one
 * sentence in one place that a support conversation can quote.
 *
 * `off` is included and is not filler. It is the sentence slice 4 earned and
 * this release makes conditional, and a tenant with tracking off is entitled to
 * see it stated rather than to see nothing.
 *
 * **`off` is the embedded-pictures wording, and only that.** Where a deployment
 * hosts pictures its last claim is false for any email with a picture, so no
 * response prints this member directly any more: the API prints
 * {@link trackingOffDisclosure}, which returns this string where pictures are
 * embedded and {@link TRACKING_DISCLOSURE_OFF_HOSTED_PICTURES} where they are
 * hosted.
 */
export const TRACKING_DISCLOSURE = {
  off: 'Tracking is off. Wend adds nothing to your messages: no tracking pixel, no rewritten links. An email you send from here makes no network request when it is opened, and Wend cannot tell you whether anybody opened it.',
  open: 'Open tracking adds an invisible 1×1 image to every HTML message. When a mail app loads that image, Amazon records an open — which means a request leaves the reader\'s device, carrying their IP address, to the host that serves the image: your own redirect subdomain over HTTPS if you have verified one, and an Amazon host over plain HTTP if you have not. The count is approximate in both directions — apps that block images hide real opens, and Apple Mail Privacy Protection loads the image for people who never looked.',
  click: 'Click tracking rewrites every link in your messages so that clicks pass through a redirect first. Recipients see the destination address only after the redirect, and the link they hover over is the tracking address — which is why Wend requires the tracking address to be a subdomain you own rather than an Amazon one. After it, each click passes through Wend’s own address, which records who clicked and adds a click id to the destination, so your website can report a conversion back.',
  clickRequiresDomain: `Click tracking needs a redirect subdomain you own. Without one, Amazon rewrites every link through ${TRACKING_DEFAULT_REDIRECT_HOST} — so every link in mail from your organisation hovers as an unfamiliar third-party address, which readers and spam filters both read as phishing.`,
} as const;

/**
 * The tracking-off sentence for a deployment that **hosts** pictures.
 *
 * {@link TRACKING_DISCLOSURE}`.off` says an email "makes no network request when
 * it is opened". That was earned by slice 4 removing remote fonts and slice 5
 * embedding pictures as CID parts, and it is still true wherever pictures are
 * embedded — which is why that constant is unchanged, word for word, and is
 * still what an embedding deployment shows. Where pictures are hosted it is
 * false for every email with a picture in it: the reader's mail app fetches the
 * picture from Wend's image host, and the request carries their IP address. A
 * tenant who has promised their members "no tracking" would repeat the false
 * version to them.
 *
 * So this says what is true there, and no more: tracking adds nothing; pictures
 * are fetched; an email without pictures still fetches nothing; and Wend does not
 * turn a picture request into an open. It does **not** claim nobody could work
 * out who opened what from the image host's requests — that rests on the host
 * keeping no access log, an operational commitment `imageBlockSchema` in
 * `./template.js` states and this sentence is not the place to vouch for.
 */
export const TRACKING_DISCLOSURE_OFF_HOSTED_PICTURES =
  "Tracking is off. Wend adds nothing to your messages: no tracking pixel, no rewritten links, and Wend cannot tell you whether anybody opened one. Pictures are the exception to “nothing is fetched”: Wend serves the pictures in an email from its own image host, so when a mail app shows them it requests them from that host, and the request carries the reader's IP address. An email with no pictures makes no network request when it is opened, and Wend does not count a picture request as an open.";

/**
 * The tracking-off sentence **that is true of this deployment**.
 *
 * Told the delivery mode rather than reading it — this package never reads the
 * environment; the API derives `TemplateImageDelivery` once and passes it. A
 * `Record` keyed on the mode, so a third mode is a compile error here rather than
 * a sentence silently describing the wrong one.
 */
const TRACKING_DISCLOSURE_OFF_BY_IMAGE_DELIVERY: Readonly<Record<TemplateImageDelivery, string>> = {
  embedded: TRACKING_DISCLOSURE.off,
  hosted: TRACKING_DISCLOSURE_OFF_HOSTED_PICTURES,
};

export function trackingOffDisclosure(imageDelivery: TemplateImageDelivery): string {
  return TRACKING_DISCLOSURE_OFF_BY_IMAGE_DELIVERY[imageDelivery];
}

/* -------------------------------------------------------------------------- */
/* Changing the settings — the refusal, and what it says                       */
/* -------------------------------------------------------------------------- */

/**
 * What a client may ask for. Three switches and nothing else.
 *
 * Deliberately **not** {@link trackingSettingsSchema}: the redirect domain's
 * state and the configuration set are facts about AWS that a client must not be
 * able to assert. A body that could carry `redirectDomain: { state: 'verified' }`
 * would let a browser talk its way past decision 3 in one field.
 */
export const vdmUpdateSchema = z.discriminatedUnion('enabled', [
  z.object({ enabled: z.literal(false) }).strict(),
  z
    .object({
      enabled: z.literal(true),
      /**
       * `z.literal(true)`, not `z.boolean()`. The request body **cannot express**
       * enabling a surcharge on the tenant's AWS bill without the price having
       * been shown — see {@link VDM_COST_DISCLOSURE}.
       */
      costAcknowledged: z.literal(true),
    })
    .strict(),
]);
export type VdmUpdate = z.infer<typeof vdmUpdateSchema>;

export const trackingSettingsUpdateSchema = z
  .object({
    open: z.boolean(),
    click: z.boolean(),
    vdm: vdmUpdateSchema,
  })
  .strict();
export type TrackingSettingsUpdate = z.infer<typeof trackingSettingsUpdateSchema>;

/** The subdomain a tenant wants to use for click redirects. */
export const trackingRedirectDomainRequestSchema = z
  .object({ domain: trackingRedirectDomainSchema })
  .strict();
export type TrackingRedirectDomainRequest = z.infer<typeof trackingRedirectDomainRequestSchema>;

/**
 * Why a requested change is refused.
 *
 *   - `click_needs_redirect_domain` — click on, no domain configured at all.
 *     The repair is the CNAME flow;
 *   - `click_needs_verified_domain` — a domain is configured and is not
 *     verified yet, or has broken. The repair is DNS, and
 *     {@link TRACKING_DOMAIN_FAILURE_MESSAGES} says which;
 *   - `needs_configuration_set` — tracking on, and nothing is provisioned in
 *     AWS. This is Wend's job and not the tenant's: it means provisioning
 *     failed or has not been attempted, and the repair is to retry it;
 *   - `needs_ses_connection` — there is no SES integration to provision into.
 *     Distinct from the above because the tenant *does* have something to do;
 *   - `vdm_cost_not_acknowledged` — belt and braces behind
 *     {@link vdmUpdateSchema}, which already makes this unrepresentable in a
 *     parsed body. It exists because the evaluator is also called with objects
 *     that did not come through the parser — a migration, a script — and this
 *     is the one refusal that protects somebody's money.
 */
export const TRACKING_CHANGE_REFUSAL_VALUES = [
  'click_needs_redirect_domain',
  'click_needs_verified_domain',
  'needs_configuration_set',
  'needs_ses_connection',
  'vdm_cost_not_acknowledged',
] as const;
export const trackingChangeRefusalSchema = z.enum(TRACKING_CHANGE_REFUSAL_VALUES);
export type TrackingChangeRefusal = z.infer<typeof trackingChangeRefusalSchema>;

export const TRACKING_CHANGE_REFUSAL_MESSAGES: Readonly<
  Record<TrackingChangeRefusal, string>
> = {
  click_needs_redirect_domain: TRACKING_DISCLOSURE.clickRequiresDomain,
  click_needs_verified_domain: `Click tracking stays off until your redirect subdomain verifies. Until it does, Amazon would rewrite every link through ${TRACKING_DEFAULT_REDIRECT_HOST}, which is what this requirement exists to prevent.`,
  needs_configuration_set:
    'Tracking needs a configuration set in your AWS account, and Wend has not created one yet. Amazon only reports opens and clicks for messages sent through one, so switching this on without it would show you zeroes for ever. Retry the setup from this screen.',
  needs_ses_connection:
    'Connect an Amazon SES account first. Tracking is set up inside your own AWS account, so there is nowhere to create it until Wend has credentials for one.',
  vdm_cost_not_acknowledged:
    'Virtual Deliverability Manager was not switched on, because its cost had not been shown and accepted. It is billed by Amazon to your account, not by Wend.',
};

/**
 * The evaluator's answer. A discriminated union, for
 * {@link import('./campaign.js').SendGuardDecision}'s reason: "refused with no
 * reason" and "allowed, and here is why it was refused" are not states worth
 * being able to build.
 *
 * The allowed arm carries the settings that will be stored, already in
 * {@link trackingSettingsSchema}'s shape — so the caller cannot construct that
 * object itself and cannot construct one the union would reject.
 */
export const trackingChangeAllowedSchema = z
  .object({ allowed: z.literal(true), settings: trackingSettingsSchema })
  .strict();

export const trackingChangeRefusedSchema = z
  .object({
    allowed: z.literal(false),
    reason: trackingChangeRefusalSchema,
    /** Safe to show a customer and safe to log. Contains no credential. */
    message: z.string(),
  })
  .strict();
export type TrackingChangeRefused = z.infer<typeof trackingChangeRefusedSchema>;

export const trackingChangeDecisionSchema = z.discriminatedUnion('allowed', [
  trackingChangeAllowedSchema,
  trackingChangeRefusedSchema,
]);
export type TrackingChangeDecision = z.infer<typeof trackingChangeDecisionSchema>;

/**
 * **The one implementation of "may this tenant turn that on".**
 *
 * Pure, and shared rather than written in the service, for the reason
 * `evaluateSendGuard` and `sesReadiness` are shared: two implementations of a
 * refusal drift, and the way they drift is that one of them stops refusing. The
 * web app calls it to decide whether the switch is even offered; the API calls
 * it to decide whether the write happens; both get the same sentence.
 *
 * It fails closed at every step, and it never partially applies a change — a
 * request that turns click on *and* VDM off is refused whole if the click half
 * is refused, because "we did half of what you asked and did not say which
 * half" is worse than a refusal.
 *
 * The narrowing at the end is not ceremony. Returning the settings through
 * {@link trackingSettingsSchema} means this function cannot hand back a
 * click-on object without a verified domain even if every check above it were
 * deleted — the type would not compile.
 */
export function evaluateTrackingChange(input: {
  readonly current: TrackingSettings;
  readonly requested: TrackingSettingsUpdate;
  readonly sesConnected: boolean;
  readonly now?: Date;
}): TrackingChangeDecision {
  const { current, requested, sesConnected } = input;
  const at = (input.now ?? new Date()).toISOString();

  const refuse = (reason: TrackingChangeRefusal): TrackingChangeDecision => ({
    allowed: false,
    reason,
    message: TRACKING_CHANGE_REFUSAL_MESSAGES[reason],
  });

  const wantsTracking = requested.open || requested.click;

  if (wantsTracking && !sesConnected) return refuse('needs_ses_connection');
  if (wantsTracking && !current.configurationSet.provisioned) {
    return refuse('needs_configuration_set');
  }
  if (requested.vdm.enabled && requested.vdm.costAcknowledged !== true) {
    return refuse('vdm_cost_not_acknowledged');
  }

  const vdm: VdmSettings = requested.vdm.enabled
    ? {
        enabled: true,
        costAcknowledgedAt:
          // Re-acknowledging an already-on VDM must not move the record of when
          // the customer was actually told. The first consent is the consent.
          current.vdm.enabled ? current.vdm.costAcknowledgedAt : at,
        costPerThousandUsdAtAcknowledgement: current.vdm.enabled
          ? current.vdm.costPerThousandUsdAtAcknowledgement
          : VDM_COST_PER_THOUSAND_USD,
      }
    : { enabled: false };

  if (!requested.click) {
    return {
      allowed: true,
      settings: {
        click: false,
        open: requested.open,
        redirectDomain: current.redirectDomain,
        configurationSet: current.configurationSet,
        deliveryReports: current.deliveryReports,
        vdm,
        updatedAt: at,
      },
    };
  }

  if (current.redirectDomain.state === 'not_configured') {
    return refuse('click_needs_redirect_domain');
  }
  if (current.redirectDomain.state !== 'verified') {
    return refuse('click_needs_verified_domain');
  }
  if (!current.configurationSet.provisioned) {
    // Unreachable given the `wantsTracking` gate above, and kept because it is
    // what narrows `configurationSet` to the provisioned arm. Deleting it would
    // not simplify the function, it would stop it compiling.
    return refuse('needs_configuration_set');
  }

  return {
    allowed: true,
    settings: {
      click: true,
      open: requested.open,
      redirectDomain: current.redirectDomain,
      configurationSet: current.configurationSet,
      deliveryReports: current.deliveryReports,
      vdm,
      updatedAt: at,
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Engagement rates — the type that cannot be rendered as an exact one         */
/* -------------------------------------------------------------------------- */

/**
 * Below this many **delivered** messages, no engagement rate is produced.
 *
 * The denominator is delivered rather than accepted, and that is the first
 * honesty in this section: nobody opens a message that bounced, and dividing
 * opens by accepted quietly folds the bounce rate into the open rate.
 *
 * The floor is {@link import('./delivery.js').RATE_SIGNIFICANCE_MINIMUM}'s
 * number rather than {@link OVERVIEW_MIN_VOLUME_FOR_RATE}'s, and for a
 * different reason than either. The overview's 1,000 falls out of AWS's 0.1%
 * complaint threshold — the smallest rate that has to be *distinguishable from
 * the line*. An open rate has no line: nothing happens at 22% that does not
 * happen at 21%, so the floor is only asking "is this noise", and a hundred
 * delivered messages is where a directional figure stops flipping on one
 * person's mail app.
 *
 * It is written out rather than imported so that a future change to the
 * campaign-results floor does not silently move this one; they answer different
 * questions and are equal by coincidence.
 */
export const ENGAGEMENT_MIN_VOLUME_FOR_RATE = 100;

/**
 * The marker. There is one member and there will not be a second.
 *
 * `confidence` is `z.literal('directional')` rather than an enum with `exact`
 * alongside it, because an exact engagement rate is not a thing that can be
 * measured — not by Wend, not by SES, not by anybody. An enum with two members
 * is an invitation to write `confidence: 'exact'` on the day somebody decides
 * the caveats are overstated.
 */
export const ENGAGEMENT_CONFIDENCE = 'directional' as const;

/**
 * Why an engagement number is wrong, and in which direction.
 *
 * These are not disclaimers. Each one is a specific mechanism with a known
 * sign, and the screen states them where the number is read rather than in a
 * footnote:
 *
 *   - `image_blocking_undercounts` — mail apps that block remote images never
 *     load the pixel, so a real open is never counted. Common in exactly the
 *     population this product mails: corporate and cooperative-bank mail
 *     clients with conservative defaults;
 *   - `mail_privacy_protection_overcounts` — Apple Mail Privacy Protection
 *     fetches the pixel on delivery, from a proxy, for every message, whether
 *     or not the person ever looks. Those opens are counted and did not happen;
 *   - `security_scanner_clicks` — a click caveat, not an open one, and the one
 *     most likely to bite here. Mail gateways at banks follow every link in
 *     every message to check it. Those clicks are counted and no person made
 *     them, which can put a click rate above an open rate;
 *   - `tracking_partially_enabled` — tracking was switched on part-way through
 *     the window, so the denominator includes messages that could never have
 *     produced an event. The rate is too low by construction, and by an amount
 *     nothing can compute.
 *
 * An open rate carries the first two, always and both — it is wrong in both
 * directions **at once**, and stating only one of them is worse than stating
 * neither, because it tells the reader which way to correct.
 */
export const ENGAGEMENT_CAVEAT_VALUES = [
  'image_blocking_undercounts',
  'mail_privacy_protection_overcounts',
  'security_scanner_clicks',
  'tracking_partially_enabled',
] as const;
export const engagementCaveatSchema = z.enum(ENGAGEMENT_CAVEAT_VALUES);
export type EngagementCaveat = z.infer<typeof engagementCaveatSchema>;

export const ENGAGEMENT_CAVEAT_MESSAGES: Readonly<Record<EngagementCaveat, string>> = {
  image_blocking_undercounts:
    'Mail apps that block images never load the tracking pixel, so opens by those readers are not counted. The real figure is higher than this one.',
  mail_privacy_protection_overcounts:
    'Apple Mail Privacy Protection loads the tracking pixel on delivery for everyone using it, whether or not they read the message. Those opens are counted here and did not happen.',
  security_scanner_clicks:
    'Security gateways at many organisations follow every link in a message to check it. Those clicks are counted here and nobody made them — which is why a click rate can come out above an open rate.',
  tracking_partially_enabled:
    'Tracking was switched on part-way through this period, so messages sent before then could not report anything. This figure is lower than the real one by an amount we cannot calculate.',
};

/** The two caveats an open rate always carries. Both, together, always. */
export const OPEN_RATE_CAVEATS: readonly EngagementCaveat[] = [
  'image_blocking_undercounts',
  'mail_privacy_protection_overcounts',
];

/** The caveat a click rate always carries. */
export const CLICK_RATE_CAVEATS: readonly EngagementCaveat[] = ['security_scanner_clicks'];

/**
 * An engagement rate. **Not interchangeable with an exact one, by construction.**
 *
 * Compare it with `OverviewRateKpi` in `./overview.js`, which is what a bounce
 * rate arrives as:
 *
 *   - `OverviewRateKpi` has `threshold` and `band`. This has neither, so it is
 *     **not assignable** to `OverviewRateKpi` and cannot be passed to a
 *     component that draws a KPI tile with a red line on it;
 *   - this has `confidence` and `caveats`, which `OverviewRateKpi` lacks, so an
 *     `OverviewRateKpi` is **not assignable here** either and a bounce rate
 *     cannot be quietly rendered through the directional component.
 *
 * That mutual non-assignability is decision 5 — *open rate must not render at
 * the same visual rank as bounce rate* — held by the compiler. Copy can be
 * reworded by anyone; a type cannot.
 *
 * There is no `band`, and adding one would be the mistake this shape exists to
 * prevent. A band is a verdict, a verdict needs a threshold, and there is no
 * threshold for an open rate: nothing is wrong at 18% and nothing is right at
 * 34%. A green chip on a directional number is a claim about a thing that was
 * not measured.
 *
 * `reading` is a {@link import('./overview.js').RateReading} from the same {@link rateReading} the rest
 * of the product uses — so "not enough volume" is `rate: null` here exactly as
 * it is everywhere else, and a component reaching for a number gets nothing to
 * format.
 */
export const engagementRateSchema = z
  .object({
    reading: rateReadingSchema,
    /** Always `'directional'`. See {@link ENGAGEMENT_CONFIDENCE}. */
    confidence: z.literal(ENGAGEMENT_CONFIDENCE),
    /**
     * Every reason this number is wrong, with its direction. Never empty for a
     * rate that exists — a directional figure with no caveats displayed is an
     * exact figure as far as the reader is concerned.
     */
    caveats: z.array(engagementCaveatSchema).min(1),
  })
  .strict();
export type EngagementRate = z.infer<typeof engagementRateSchema>;

/**
 * The only supported way to build one.
 *
 * `caveats` is a required parameter with no default, so a caller cannot produce
 * an uncaveated engagement rate by forgetting an argument.
 * {@link OPEN_RATE_CAVEATS} and {@link CLICK_RATE_CAVEATS} are what to pass.
 */
export function engagementRate(input: {
  readonly numerator: number;
  readonly denominator: number;
  readonly caveats: readonly EngagementCaveat[];
  readonly extraCaveats?: readonly EngagementCaveat[];
  readonly minimum?: number;
}): EngagementRate {
  const caveats = [...new Set([...input.caveats, ...(input.extraCaveats ?? [])])];
  return {
    reading: rateReading(
      input.numerator,
      input.denominator,
      input.minimum ?? ENGAGEMENT_MIN_VOLUME_FOR_RATE,
    ),
    confidence: ENGAGEMENT_CONFIDENCE,
    caveats,
  };
}

/* -------------------------------------------------------------------------- */
/* The engagement panel — absent rather than zero                              */
/* -------------------------------------------------------------------------- */

/**
 * Why there is no engagement to show.
 *
 * The Overview's header already makes this argument for slice 10 and it is the
 * same argument: **a tile reading "0% opened" when the truth is "we do not
 * measure this" is a lie the reader has no way to detect.** Now that the
 * measurement is buildable the failure mode is worse, not better, because the
 * tile is genuinely capable of showing a real zero — so the *reason* has to
 * ride on the wire, and the `tracked: false` arm below has nowhere to put a
 * number.
 *
 *   - `tracking_off` — this tenant has both switches off. The correct thing to
 *     print is {@link TRACKING_DISCLOSURE}`.off`, not a zero and not a dash;
 *   - `open_tracking_off` / `click_tracking_off` — one of the two is on. The
 *     panel shows the one that is measured and says why the other is missing;
 *   - `no_delivered_messages` — tracking is on and nothing has been delivered
 *     in the window, **and nothing is still waiting to be**: either nothing was
 *     sent, or everything that was has come back as a bounce or a failure.
 *     There is no denominator, which is different from a denominator of zero
 *     engagements;
 *   - `no_delivery_reports` — tracking is on, email was accepted in the window,
 *     and not one delivery has been reported while some of it is still waiting
 *     for an outcome. Some may have come back as bounces — which is why its
 *     sentence says no *delivery* has been reported rather than no report at
 *     all, after review found a card saying the second beside a bounce count. Split out of `no_delivered_messages` in slice 20,
 *     because on a deployment whose delivery notifications do not reach Wend —
 *     the live one, until slice 19 — `delivered` is zero for every message ever
 *     sent, and "no email was delivered" was a false statement about all of
 *     them. `delivered` is only ever set by a delivery event;
 *   - `awaiting_first_events` — tracking was switched on very recently and no
 *     event has arrived yet. Distinct from `no_delivered_messages` because the
 *     repair is *wait* and there is no repair for the other.
 */
export const ENGAGEMENT_UNAVAILABLE_VALUES = [
  'tracking_off',
  'open_tracking_off',
  'click_tracking_off',
  'no_delivered_messages',
  'no_delivery_reports',
  'awaiting_first_events',
] as const;
export const engagementUnavailableSchema = z.enum(ENGAGEMENT_UNAVAILABLE_VALUES);
export type EngagementUnavailable = z.infer<typeof engagementUnavailableSchema>;

export const ENGAGEMENT_UNAVAILABLE_MESSAGES: Readonly<
  Record<EngagementUnavailable, string>
> = {
  tracking_off: TRACKING_DISCLOSURE.off,
  open_tracking_off:
    'Open tracking is off for this workspace, so Wend does not know whether anybody opened these messages. This is not zero opens — it is no measurement.',
  click_tracking_off:
    'Click tracking is off for this workspace, so Wend does not know whether anybody followed a link. This is not zero clicks — it is no measurement.',
  no_delivered_messages:
    'No email was delivered in this period, so there is nothing that could have been opened.',
  no_delivery_reports:
    'No delivery has been reported for the email sent in this period, so Wend cannot tell whether any of it arrived — and there is nothing to measure an open or a click against. This is not zero delivered; it is not known.',
  awaiting_first_events:
    'Tracking is on and no opens or clicks have arrived yet. Amazon reports these within minutes of them happening.',
};

/**
 * One measured engagement metric.
 *
 * `unique` and `total` are both here and they do not agree, deliberately. The
 * rate is built from `unique` — **distinct message ids**, the same rule every
 * count in `./overview.js` follows — because one person opening a newsletter
 * six times on the train is one open for the purpose of "did this land", and
 * counting rows would put an open rate over 100% and make the number
 * unreadable. `total` is carried anyway because it is the honest raw figure and
 * because their ratio is itself informative.
 */
export const engagementMetricSchema = z
  .object({
    /** Distinct messages with at least one event. The rate's numerator. */
    unique: z.number().int().min(0),
    /** Every event row. Always >= `unique`. Never a rate's numerator. */
    total: z.number().int().min(0),
    rate: engagementRateSchema,
  })
  .strict();
export type EngagementMetric = z.infer<typeof engagementMetricSchema>;

/**
 * The panel, in whichever of its two states it is in.
 *
 * A discriminated union on `tracked` so the `tracked: false` arm has **no field
 * a number could live in**. That is the enforcement: a mapper that wanted to
 * render a zero would have to change this file first.
 *
 * `open` and `click` are independently nullable inside the tracked arm, because
 * a tenant may have opens on and clicks off. `null` there means "not measured",
 * and the arm carries `unavailable` alongside so the screen can say which of
 * {@link ENGAGEMENT_UNAVAILABLE_MESSAGES} applies to the missing half.
 */
export const engagementPanelUntrackedSchema = z
  .object({
    tracked: z.literal(false),
    reason: engagementUnavailableSchema,
    /** The sentence to print. From {@link ENGAGEMENT_UNAVAILABLE_MESSAGES}. */
    message: z.string(),
  })
  .strict();

export const engagementPanelTrackedSchema = z
  .object({
    tracked: z.literal(true),
    /**
     * Delivered email in the window — the denominator of both rates, and not
     * `accepted`. Nobody opens a message that bounced.
     */
    delivered: z.number().int().min(0),
    /** Null when open tracking is off for this tenant. Never a zero stand-in. */
    open: engagementMetricSchema.nullable(),
    /** Null when click tracking is off for this tenant. Never a zero stand-in. */
    click: engagementMetricSchema.nullable(),
    /**
     * Why a null above is null. Empty when both are measured.
     *
     * An array rather than a single member because both halves can be missing
     * for different reasons at once — open off, clicks awaiting their first
     * event.
     */
    unavailable: z.array(engagementUnavailableSchema),
    /** Days the counts cover. Matches the Overview's window by default. */
    windowDays: z.number().int().min(1),
  })
  .strict();

export const engagementPanelSchema = z.discriminatedUnion('tracked', [
  engagementPanelUntrackedSchema,
  engagementPanelTrackedSchema,
]);
export type EngagementPanel = z.infer<typeof engagementPanelSchema>;

/** Builds the untracked arm with its sentence attached, so the two cannot drift. */
export function engagementUnavailable(reason: EngagementUnavailable): EngagementPanel {
  return { tracked: false, reason, message: ENGAGEMENT_UNAVAILABLE_MESSAGES[reason] };
}

/**
 * The untracked arm for a workspace with **both switches off** — the one reason
 * whose sentence depends on the deployment as well as on the tenant.
 *
 * `ENGAGEMENT_UNAVAILABLE_MESSAGES.tracking_off` is the embedded wording, "makes
 * no network request when it is opened", and it is false for an email with
 * pictures where pictures are hosted. So the API builds this arm through here,
 * told the mode, and the sentence is {@link trackingOffDisclosure}'s — the same
 * one the tracking card prints, so the Overview and Integrations cannot disagree
 * about the same workspace.
 */
export function engagementTrackingOff(imageDelivery: TemplateImageDelivery): EngagementPanel {
  return { tracked: false, reason: 'tracking_off', message: trackingOffDisclosure(imageDelivery) };
}

/* -------------------------------------------------------------------------- */
/* VDM's ISP breakdown — aggregate, and it must stay that way                  */
/* -------------------------------------------------------------------------- */

/**
 * **NOTHING IN THIS SECTION MAY BE JOINED TO A CONTACT. THERE IS NOTHING TO
 * JOIN IT TO, AND THAT IS THE POINT.**
 *
 * SESv2 `BatchGetMetricData` in namespace `VDM` returns **counts**, dimensioned
 * by `EMAIL_IDENTITY`, `CONFIGURATION_SET` and `ISP`. There is no recipient in
 * the response, no message id, and no way to ask for one. "142 bounces at
 * gmail.com" is the entire content of a row.
 *
 * That is a limit, and this file treats it as a guarantee instead — because the
 * tempting move is to *reconstruct* the identity, and it would work. A tenant
 * whose gmail.com bounce count is 3 and who mailed four gmail.com addresses
 * this week has narrowed it to four people; do it over a small enough segment
 * and it narrows to one. So the rules are written down rather than left to be
 * inferred from the shape:
 *
 *   1. **No suppression may be driven by anything here.** A suppression is a
 *      permanent statement about one address and this data does not contain
 *      one. `delivery_events` remains the only source of a suppression, as
 *      `./delivery.js` sets out;
 *   2. **No segment may be built from anything here.** Not "contacts at ISPs
 *      with a high bounce rate", not any of its cousins. That query is the
 *      reconstruction above, wearing a feature's clothes;
 *   3. **Nothing else may depend on it.** It is one panel. VDM is a per-tenant
 *      opt-in that costs the tenant money, so anything that depended on it
 *      would be a product feature that half of all tenants cannot have — and
 *      would create pressure to switch it on for them;
 *   4. **It is never the record.** Everything VDM reports except the ISP
 *      dimension, the SNS event stream already has at finer grain and with
 *      identity. Where the two disagree, `delivery_events` is right.
 *
 * `aggregate` and `carriesContactIdentity` are literals on
 * {@link vdmIspBreakdownAvailableSchema} so the contract cannot express a
 * per-recipient VDM row. It is not enforcement of rules 1–3 — nothing in a
 * schema can enforce a query somebody writes — but it does mean the reviewer
 * grepping for how identity might have got in finds a `z.literal(false)` and
 * this paragraph.
 *
 * **What it is actually for**, and it is worth having: *is the bounce rate up
 * everywhere, or only at Gmail?* That is the question that turns a frightening
 * number into an action, and it is the one thing the event stream cannot
 * answer.
 */

/**
 * One ISP's counts over the window.
 *
 * `isp` is `z.string()` and not an enum for the reason
 * `sesIdentitySchema.identityType` is not: it is AWS's vocabulary, AWS adds to
 * it, and an ISP Amazon starts reporting next year must not fail the parse of
 * an otherwise good panel. A third party's vocabulary is data, not our enum.
 *
 * Permanent and transient bounces are separate here as they are everywhere else
 * in this codebase — see `isHardBounce` in `./delivery.js`. A single bounce
 * figure per ISP would hide the only distinction that matters: full mailboxes
 * at one provider are a different story from dead addresses at one provider.
 */
export const vdmIspRowSchema = z
  .object({
    /** e.g. `gmail.com`, `outlook.com`, `yahoo.com`. AWS's spelling, unaltered. */
    isp: z.string().min(1),
    sends: z.number().int().min(0),
    deliveries: z.number().int().min(0),
    permanentBounces: z.number().int().min(0),
    transientBounces: z.number().int().min(0),
    complaints: z.number().int().min(0),
    /**
     * Delivered ÷ sent at this ISP. A {@link import('./overview.js').RateReading}, so a provider with
     * eleven messages produces `rate: null` and the panel shows the counts.
     */
    deliveryRate: rateReadingSchema,
    /** Permanent and transient together ÷ sent. The number the panel sorts on. */
    bounceRate: rateReadingSchema,
    complaintRate: rateReadingSchema,
    /**
     * Null unless open tracking is on — VDM's engagement metrics *are* the
     * pixel and the rewritten links, so with tracking off there is nothing here
     * however much VDM costs.
     */
    openRate: engagementRateSchema.nullable(),
    /** Null unless click tracking is on. Same reason. */
    clickRate: engagementRateSchema.nullable(),
  })
  .strict();
export type VdmIspRow = z.infer<typeof vdmIspRowSchema>;

/**
 * Below this many sends at one ISP, that ISP's rates are refused.
 *
 * Lower than {@link OVERVIEW_MIN_VOLUME_FOR_RATE} on purpose, and the reason is
 * the shape of the question. The Overview's floor guards a single workspace-wide
 * figure somebody would pause sending over. This panel is read comparatively —
 * *Gmail is fine and Yahoo is not* — so its job is to let a small provider
 * appear at all rather than to be authoritative about it. A tenant mailing
 * cooperative banks has a long tail of small regional domains, and a floor of a
 * thousand would delete every one of them from the panel and leave a chart that
 * says "Gmail" and nothing else.
 */
export const VDM_MIN_VOLUME_FOR_ISP_RATE = 50;

/**
 * Why the ISP panel has nothing to show.
 *
 * `needs_tracking_on` is the awkward one and it is here rather than folded into
 * `no_data_yet` because the two say opposite things to the reader. Amazon
 * reports these figures **against a configuration set**, Wend asks about the
 * tracking set, and a message is only inside the tracking set when it was sent
 * naming it — which, by slice 11's whole safety property, happens only when open
 * or click tracking is on. (Since slice 19 an untracked message is sent through
 * the delivery reports set instead, and Wend does not read VDM figures for that
 * set.) So a tenant with VDM on and both switches off has every metric come back
 * zero for ever. `no_data_yet` would tell them to wait; nothing about waiting
 * resolves it.
 */
export const VDM_UNAVAILABLE_VALUES = [
  'not_enabled',
  'needs_configuration_set',
  'needs_tracking_on',
  'no_data_yet',
  'lookup_failed',
] as const;
export const vdmUnavailableSchema = z.enum(VDM_UNAVAILABLE_VALUES);
export type VdmUnavailable = z.infer<typeof vdmUnavailableSchema>;

export const VDM_UNAVAILABLE_MESSAGES: Readonly<Record<VdmUnavailable, string>> = {
  not_enabled: `Virtual Deliverability Manager is off for this workspace. ${VDM_COST_DISCLOSURE}`,
  needs_configuration_set:
    'Amazon reports these figures against a configuration set, and Wend has not created one in your AWS account yet.',
  needs_tracking_on:
    'Amazon reports these figures against the configuration set a message was sent through, and Wend reads them from your tracking configuration set — which messages from this workspace go through only while open or click tracking is on. With both off there is nothing there for Amazon to report on, and waiting will not change that. Switching open tracking on is what puts your sends inside the tracking configuration set. Nothing else here does.',
  no_data_yet: `Amazon has no figures for this period yet. Virtual Deliverability Manager starts collecting from the moment it is enabled in your own AWS account and does not backfill — if it has never been enabled there, this stays empty however long you wait. ${VDM_ENABLE_STEP}`,
  lookup_failed:
    'We could not read these figures from Amazon just now. This says nothing about your deliverability; the panel will fill in on the next load.',
};

export const vdmIspBreakdownUnavailableSchema = z
  .object({
    available: z.literal(false),
    reason: vdmUnavailableSchema,
    message: z.string(),
  })
  .strict();

export const vdmIspBreakdownAvailableSchema = z
  .object({
    available: z.literal(true),
    /**
     * Always `'ISP'`. A literal because this shape describes one dimension of
     * `BatchGetMetricData` and a field that could say `EMAIL_IDENTITY` would
     * invite a second, differently-shaped thing to be poured into it.
     */
    dimension: z.literal('ISP'),
    /**
     * **Always true.** Every row is a count over many recipients. See the
     * section header: nothing here may be joined to a contact.
     */
    aggregate: z.literal(true),
    /**
     * **Always false, and it cannot be otherwise.** AWS returns no recipient in
     * this response and Wend does not add one. A schema cannot stop somebody
     * writing a join, but it can make the claim explicit and greppable.
     */
    carriesContactIdentity: z.literal(false),
    /** Descending by sends. Long tails are real; the panel truncates, it does not sum. */
    rows: z.array(vdmIspRowSchema),
    /**
     * True when more ISPs existed than were returned. The remainder is **not**
     * rolled into an "Other" row: a bucket of unlike providers has no useful
     * rate and reads as a real one.
     */
    truncated: z.boolean(),
    windowDays: z.number().int().min(1),
    /**
     * When AWS was asked. **This one is a live call**, unlike everything on
     * `GET /overview`, which is why the panel is a separate read — see the file
     * header.
     */
    retrievedAt: isoDateTimeSchema,
    /** The floor the rates above were tested against, so the panel can say it. */
    minimumVolume: z.number().int().min(0),
  })
  .strict();

export const vdmIspBreakdownSchema = z.discriminatedUnion('available', [
  vdmIspBreakdownUnavailableSchema,
  vdmIspBreakdownAvailableSchema,
]);
export type VdmIspBreakdown = z.infer<typeof vdmIspBreakdownSchema>;

/** Builds the unavailable arm with its sentence attached, so the two cannot drift. */
export function vdmUnavailable(reason: VdmUnavailable): VdmIspBreakdown {
  return { available: false, reason, message: VDM_UNAVAILABLE_MESSAGES[reason] };
}

/* -------------------------------------------------------------------------- */
/* The responses                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `GET /tracking` and `PUT /tracking` — what the Integrations screen reads and
 * writes.
 *
 * The settings, plus the two things a screen needs in order to explain them
 * without recomputing anything: whether SES is connected at all, and the
 * disclosure text the switch is obliged to show.
 *
 * `disclosure` travels on the wire rather than being imported by the frontend.
 * It costs a few hundred bytes and it buys one thing: the API's audit entry and
 * the screen's copy are provably the same sentence, so "what were they told"
 * has an answer that does not depend on which build of the frontend was
 * deployed that day.
 */
export const trackingResponseSchema = z
  .object({
    settings: trackingSettingsSchema,
    /** False means every switch is refused with `needs_ses_connection`. */
    sesConnected: z.boolean(),
    /** {@link TRACKING_DISCLOSURE}, carried so the copy cannot fork. */
    disclosure: z
      .object({
        off: z.string(),
        open: z.string(),
        click: z.string(),
        clickRequiresDomain: z.string(),
      })
      .strict(),
    /** {@link VDM_COST_DISCLOSURE}, and the figure it was built from. */
    vdmCost: z
      .object({
        disclosure: z.string(),
        perThousandUsd: z.number().min(0),
        sendPerThousandUsd: z.number().min(0),
      })
      .strict(),
  })
  .strict();
export type TrackingResponse = z.infer<typeof trackingResponseSchema>;

/**
 * `GET /overview/engagement`. The two panels, read together and separately from
 * the dashboard.
 *
 * Separate from `GET /overview` because {@link vdmIspBreakdownSchema}'s
 * available arm is filled by a live `BatchGetMetricData` call on the tenant's
 * own AWS account, and slice 10's whole argument is that **drawing the
 * dashboard must not spend a tenant's provider rate limit or fail because AWS
 * is slow**. Folding this in would have quietly repealed that.
 *
 * `tracking` rides along so the panel can render its own explanation — which
 * switch is off, and what the switch would do — without a second request and
 * without the frontend keeping its own copy of the tenant's settings.
 */
export const engagementResponseSchema = z
  .object({
    generatedAt: isoDateTimeSchema,
    windowDays: z.number().int().min(1),
    /** What is on. The panel's `tracked: false` reason is derived from this. */
    tracking: trackingSettingsSchema,
    /**
     * How this deployment carries the pictures in an email — the API's one
     * derived answer, `imageHost.delivery`.
     *
     * On an engagement response because the Overview says what a workspace's mail
     * fetches when it is opened, and with tracking off that depends on this as
     * much as on `tracking`: where pictures are hosted, an email with a picture
     * makes a request to Wend's image host. Without it the screen could only
     * print one of two sentences and be wrong on half the deployments.
     */
    imageDelivery: templateImageDeliverySchema,
    engagement: engagementPanelSchema,
    isp: vdmIspBreakdownSchema,
  })
  .strict();
export type EngagementResponse = z.infer<typeof engagementResponseSchema>;

/**
 * The window engagement is read over. The Overview's, so the two screens agree.
 *
 * Re-exported as a named constant rather than left as an import so a reader of
 * this file can see that the answer is thirty days without opening another one,
 * and so that a future divergence is a deliberate edit here rather than a
 * default drifting underneath.
 */
export const ENGAGEMENT_WINDOW_DAYS = OVERVIEW_ACTIVITY_WINDOW_DAYS;
